import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    Spin,
    Drawer,
    Form,
    Input,
    Row,
    Col,
    Button,
    Divider,
    Alert,
    Icon,
    Select,
} from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import { DrawerProps } from 'antd/lib/drawer';
import { ButtonProps } from 'antd/lib/button';

import { MainReducerState } from '../../store/reducers';
import { Section } from '../../store/api/apiTypes';
import {
    update, list, create, details, SectionsState,
    getSectionCreateState,
    getSectionUpdateState, getSectionDetailsState, getSectionsListState,
} from '../../store/actions/sections';
import { getErrorMessageFromState } from '../../utils';

interface SectionDrawerProps extends FormComponentProps {
    created: SectionsState['create'];
    updated: SectionsState['update'];
    sections: SectionsState['list'];
    loadedSection: SectionsState['details'];
    createSection: typeof create.trigger;
    getSection: typeof details.trigger;
    updateSection: typeof update.trigger;
    getSections: typeof list.trigger;
    sectionId?: string;
    isVisible: boolean;
    onClose: () => void;
    onUpdateSuccess?: () => void;
    action?: string;
}

const SectionDrawerForm: FC<SectionDrawerProps> = ({
    created,
    updated,
    sections,
    loadedSection,
    createSection,
    getSection,
    getSections,
    updateSection,
    sectionId,
    isVisible,
    onClose,
    onUpdateSuccess,
    form,
    action,
}) => {

    const [isEditing, setIsEditing] = useState(false);
    const [parentId, setParentId] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (created.success || updated.success) {

            form.resetFields();
            if (typeof onUpdateSuccess === 'function') {
                onUpdateSuccess();
            }
            onClose();
        }
    }, [created.success, updated.success]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (sections.success) {
            setParentId(undefined);
        }
    }, [sections.success]);

    useEffect(() => {
        if (loadedSection.success && loadedSection.data && loadedSection.data.parent) {
            setParentId(loadedSection.data.parent.id);
        } else {
            setParentId(undefined);
        }
    }, [loadedSection.success, loadedSection.data]);

    useEffect(() => {
        if (isVisible) {
            getSections();
            if (action === 'edit') {
                getSection({ id: sectionId });
                setIsEditing(true);
            } else {
                setParentId(sectionId);
                setIsEditing(false);
            }
        }
    }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

    const onDrawerClose: DrawerProps['onClose'] & ButtonProps['onClick'] = () => {
        onClose();
        form.resetFields();
    };

    const onSubmit = (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }
        form.validateFieldsAndScroll(async (err, val) => {
            if (err) {
                return;
            }
            const payload = {
                ...val,
            };

            if (isEditing) {
                updateSection({ id: sectionId, data: payload });
            } else {
                createSection({ data: payload });
            }
        });
    };

    const onSectionChange = (newSection: Section) => {
        form.setFieldsValue({ section: newSection });
    };

    const onPictoChange = (newPicto: string) => {
        form.setFieldsValue({ picto: newPicto });
    };

    const { getFieldDecorator } = form;
    const createErrorMessage = getErrorMessageFromState(created);
    const updateErrorMessage = getErrorMessageFromState(updated);

    const pictos = [
        'line-chart',
        'area-chart',
        'pie-chart',
        'bar-chart',
        'heat-map',
        'rise',
        'heart',
        'fund',
        'bank',
        'calculator',
        'bell',
        'bulb',
        'cloud',
        'car',
        'appstore',
        'calendar',
        'camera',
        'compass',
        'eye',
        'fire',
        'home',
        'idcard',
        'message',
        'lock',
        'picture',
        'star',
        'tag',
        'tool',
        'audit',
        'global',
        'paper-clip',
    ];

    return (
        <>
            <Drawer
                title={isEditing ? `Edition de la rubrique` : `Creation de rubrique`}
                width={700}
                onClose={onDrawerClose}
                visible={isVisible}
            >
                <Spin spinning={loadedSection.loading} tip="Chargement...">
                    {(!isEditing || (!loadedSection.loading && loadedSection.data)) && (
                        <Form onSubmit={onSubmit} layout="vertical">
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Nom">
                                        {getFieldDecorator('name', {
                                            rules: [{
                                                required: true,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && loadedSection.data ?
                                                loadedSection.data.name : '',
                                        })(
                                            <Input placeholder="Nom de la rubrique" />,
                                        )}
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="Rubrique Parente">
                                        {getFieldDecorator('parentId', {
                                            rules: [{
                                                required: false,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && loadedSection.data && loadedSection.data.parent ?
                                                loadedSection.data.parent.id : parentId,
                                        })((
                                            <Select
                                                allowClear={true}
                                                filterOption={false}
                                                placeholder="Choisissez une rubrique"
                                                onChange={onSectionChange}
                                            >
                                                {sections.data &&
                                                    sections.data.items.map((s: Section) => (
                                                        <Select.Option
                                                            value={s.id}
                                                            key={s.id}
                                                        >
                                                            {s.name}
                                                        </Select.Option>
                                                    ))}
                                            </Select>
                                        ))}
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={7}>
                                    <Form.Item label="Nom abrégé  (15 car. max)">
                                        {getFieldDecorator('short', {
                                            rules: [{
                                                required: true,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && loadedSection.data ?
                                                loadedSection.data.short : '',
                                        })(
                                            <Input placeholder="Nom abrégé" />,
                                        )}
                                    </Form.Item>
                                </Col>
                                <Col push={1} span={4}>
                                    <Form.Item label="Icône">
                                        {getFieldDecorator('picto', {
                                            rules: [{
                                                required: true,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && loadedSection.data ?
                                                loadedSection.data.picto : '',
                                        })((
                                            <Select
                                                allowClear={true}
                                                filterOption={false}
                                                placeholder="Choisissez une icône"
                                                onChange={onPictoChange}
                                            >
                                                {pictos.map((s: string) => (
                                                        <Select.Option
                                                            value={s}
                                                            key={s}
                                                        >
                                                            <Icon type={s}/>
                                                        </Select.Option>
                                                    ))}
                                            </Select>
                                        ))}
                                    </Form.Item>
                                </Col>
                            </Row>
                            <hr />
                            {!created.loading && created.error && (
                                <Form.Item>
                                    <Alert
                                        message={'Une erreur est survenue: ' + createErrorMessage}
                                        type="error"
                                    />
                                </Form.Item>
                            )}
                            {!updated.loading && updated.error && (
                                <Form.Item>
                                    <Alert
                                        message={'Une erreur est survenue: ' + updateErrorMessage}
                                        type="error"
                                    />
                                </Form.Item>
                            )}
                            <div className="form-actions">
                                <Button
                                    htmlType="submit"
                                    type="primary"
                                    onClick={onSubmit}
                                    disabled={updated.loading || created.loading}
                                    loading={updated.loading || created.loading}
                                >
                                    {isEditing ? 'Mettre à jour' : 'Ajouter'}
                                </Button>
                                <Divider type="vertical" />
                                <Button onClick={onDrawerClose} type="ghost">
                                    Annuler
                                </Button>
                            </div>
                        </Form>
                    )}
                </Spin>
            </Drawer>
        </>
    );
};

const mapStateToProps = (state: MainReducerState) => ({
    created: getSectionCreateState(state),
    updated: getSectionUpdateState(state),
    sections: getSectionsListState(state),
    loadedSection: getSectionDetailsState(state),
});

const SectionDrawer = Form.create<SectionDrawerProps>()(SectionDrawerForm);

export default connect(
    mapStateToProps,
    {
        createSection: create.trigger,
        getSection: details.trigger,
        getSections: list.trigger,
        updateSection: update.trigger,
    },
)(SectionDrawer);
